import moment from 'moment';

import { CalendarGap } from '@atom/components/workOrders/workOrdersCalendarNew/weekViewUtils';
import {
  addMillisecondToDate,
  convertMinutesToMilliseconds,
  roundUpByMilliseconds,
} from '@atom/utilities/timeUtilities';

interface Props {
  fromCalendarDate: Date;
  fromCalendarGap: CalendarGap;
}

const SIXTY_MINUTES_IN_MILLISECONDS = convertMinutesToMilliseconds(60);
const FIFTEEN_MINUTES_IN_MILLISECONDS = convertMinutesToMilliseconds(15);

const dayAtCurrentTime = (day: Date): Date => {
  const targetDate = moment(day);
  const currentTime = moment();

  return new Date(
    targetDate
      .set({
        hour: currentTime.hour(),
        minute: currentTime.minute(),
        second: currentTime.second(),
        millisecond: currentTime.millisecond(),
      })
      .valueOf(),
  );
};

export const getStartAndDueDateInitialValues = ({
  fromCalendarDate,
  fromCalendarGap,
}: Props): {
  plannedStartDate: Date;
  dueDate: Date;
} => {
  if (!!fromCalendarGap) {
    // use this block for gap-based initialization
    const roundedStartDate = roundUpByMilliseconds(
      new Date(fromCalendarGap.plannedStartDate),
      FIFTEEN_MINUTES_IN_MILLISECONDS,
    );
    const roundedDueDate = roundUpByMilliseconds(
      new Date(fromCalendarGap.dueDate),
      FIFTEEN_MINUTES_IN_MILLISECONDS,
    );

    const getFirstGapValues = () => {
      // plannedStartDate is an hour before gap dueDate for first gap
      // - if roundedDueDate within first hour of day, set plannedStartDate to start of day
      const calculatedStartTime = addMillisecondToDate(
        roundedDueDate,
        -SIXTY_MINUTES_IN_MILLISECONDS,
      );
      const startIsDifferentDay = !moment(calculatedStartTime).isSame(
        roundedDueDate,
        'day',
      );
      return {
        plannedStartDate: startIsDifferentDay
          ? moment(roundedDueDate).startOf('day').toDate()
          : calculatedStartTime,
        // dueDate is same as end of first gap
        dueDate: roundedDueDate,
      };
    };

    const getGapValues = () => {
      // plannedStartDate is same as start of gap for remaining gaps
      // - if roundedStartDate within last hour of day, set dueDate to end of day
      const calculatedEndTime = addMillisecondToDate(
        roundedStartDate,
        SIXTY_MINUTES_IN_MILLISECONDS,
      );
      const endIsDifferentDay = !moment(calculatedEndTime).isSame(
        roundedStartDate,
        'day',
      );
      return {
        plannedStartDate: roundedStartDate,
        // dueDate is an hour after gap plannedStartDate for remaining gaps
        dueDate: endIsDifferentDay
          ? moment(roundedStartDate).endOf('day').toDate()
          : calculatedEndTime,
      };
    };

    return fromCalendarGap.firstGap ? getFirstGapValues() : getGapValues();
  }

  // Round up current date to closest 15 minute
  const roundedDate = roundUpByMilliseconds(
    fromCalendarDate ? dayAtCurrentTime(fromCalendarDate) : new Date(),
    FIFTEEN_MINUTES_IN_MILLISECONDS,
  );

  // Start date is current rounded date
  const plannedStartDate = roundedDate;

  // Due date is current rounded date + 60 minutes
  const dueDate = addMillisecondToDate(
    roundedDate,
    SIXTY_MINUTES_IN_MILLISECONDS,
  );

  // If true, the dates are on the same day
  const datesAreSameDay = moment(plannedStartDate).isSame(dueDate, 'day');

  return {
    plannedStartDate,
    // If dueDate is not same day as plannedStartDate, then do not populate
    dueDate: datesAreSameDay ? dueDate : null,
  };
};
